<template>
  <div v-if="cmsData" class="mx-5">
    <Breadcrumbs
      :links="[
        { value: translatedOverviewSlugName, to: outfitsOverviewData?.slug },
        {
          value: translatedSlugName,
          to: `${outfitsOverviewData?.slug}/${cmsData?.slug}`,
        },
      ]"
    />
    <div
      class="row-auto -mx-5 grid grid-cols-1 gap-x-0 gap-y-10 md:grid-cols-2 md:gap-12 lg:gap-x-32 lg:gap-y-16"
    >
      <div class="order-1 col-span-1 row-span-1">
        <InteractableImage class="h-full" :blok="cmsData.content.Image[0]" />
      </div>

      <div
        v-if="cmsData.content.fashionTip !== ''"
        class="order-3 col-span-1 row-span-1 flex flex-col gap-5 md:flex-row"
      >
        <div class="flex min-w-full flex-col items-center md:min-w-[209px]">
          <img
            src="~/assets/images/fashion-specialist.png"
            alt="Fashion specialist"
            class="h-auto max-w-[100px]"
          />
          <p
            class="mt-5 text-center text-sm font-normal leading-4 text-black-40 md:mt-2 md:text-lg md:leading-6"
          >
            Nada,
            <br />
            {{ $t('outfits.fashion_specialist') }}
          </p>
        </div>
        <p
          class="px-12 text-center text-sm font-normal leading-4 md:px-0 md:text-left md:text-lg md:leading-6"
        >
          {{ cmsData.content.fashionTip }}
        </p>
      </div>

      <div class="order-2 col-span-1 row-span-2 px-4 md:pr-[20%]">
        <h2
          class="mb-5 px-20 text-center text-lg font-medium leading-6 tracking-wider md:mb-10 md:px-0 md:text-left md:text-xl md:leading-8"
        >
          {{ $t('outfits.this_combination_consists_of') }}:
        </h2>
        <ProductList
          v-if="products"
          class="grid grid-cols-2 gap-1"
          :loading="productsFetching"
          :products="products"
          :list-type="trackingListType"
          :list-name="trackingListName"
        >
          <template #product="{ product, index, loading }">
            <NuxtLazyHydrate when-idle>
              <ProductCard
                :id="product.id"
                :key="`product-${product.id}`"
                data-test-id="product-item"
                :product="product"
                :siblings="getSiblingsColors(product) as ProductSibling[]"
                :colors="getProductAndSiblingsColors(product, 'colorRange')"
                :link="getProductDetailRoute(product)"
                :image="
                  getHoverImage(product.images) || getFrontImage(product.images)
                "
                :loading="loading"
                :price="getLowestPriceBetweenVariants(product)"
                :show-price-reduction="_isActiveSalePeriod"
                @click:product="onProductClick(product)"
                @intersect:product="collectIntersection(product, index)"
              >
                <template #header-actions>&nbsp;</template>
                <template #header-image="{ image, productName }">
                  <a
                    v-if="image"
                    href="#"
                    @click.prevent="onProductClick(product)"
                  >
                    <ProductImage
                      fit="contain"
                      :image="image"
                      :alt="productName"
                      sizes="sm:100vw"
                      :should-trim="false"
                      is-centered
                      class="absolute inset-0"
                    />
                  </a>
                </template>

                <template #description-title-price="{ brand, price, nameHtml }">
                  <a href="#" @click.prevent="onProductClick(product)">
                    <div class="pb-0.5 pt-[9px] lg:pt-[15px]">
                      <p class="truncate uppercase">
                        {{ brand }}
                      </p>
                      <p
                        class="description truncate font-light"
                        data-qa="product-title"
                        v-html="nameHtml"
                      />

                      <ProductPrice
                        v-if="price"
                        :price="price"
                        :applied-reductions="price.appliedReductions"
                        :show-price-reduction="_isActiveSalePeriod"
                        type="whisper"
                        size="md"
                      />
                    </div>
                  </a>
                </template>

                <template #header-badge>
                  <div class="card-container group-hover:hidden">
                    <ProductBadge
                      v-if="isProductSustainable(product)"
                      :badge-label="$t('badge_labels.sustainable')"
                    />
                    <ProductBadge
                      v-if="product.isNew"
                      :badge-label="$t('badge_labels.new')"
                      class="card-container--product-badge__new bg-white text-black"
                    />

                    <template v-if="_isActiveSalePeriod">
                      <ProductBadge
                        v-for="(
                          campaign, idx
                        ) in getSalesRelativeAmountByCategory(
                          product,
                          'campaign',
                        )"
                        :key="`campaign-${idx}`"
                        class="w-max bg-[#ff6e17]"
                        :badge-label="`-${Math.round(
                          campaign.amount.relative * 100,
                        )}% EXTRA`"
                      />
                      <ProductBadge
                        v-for="(sale, idx) in getSalesRelativeAmountByCategory(
                          product,
                          'sale',
                        )"
                        :key="`sale-${idx}`"
                        :badge-label="`-${Math.round(
                          sale.amount.relative * 100,
                        )}%`"
                        class="card-container--product-badge__sale bg-red text-white"
                      />
                    </template>
                  </div>
                </template>
              </ProductCard>
            </NuxtLazyHydrate>
          </template>
        </ProductList>
      </div>
    </div>

    <SelectionModal
      :is-modal-open="isSelectionModalOpen"
      :selected-product="selectedProduct"
      :products="products"
      :list-name="trackingListName"
      @close-modal="isSelectionModalOpen = false"
    />

    <template v-if="postListingContent">
      <component
        :is="postContent.component"
        v-for="postContent in postListingContent"
        :key="postContent._uid"
        class="mb-8 mt-20 md:mb-5 md:mt-32"
        :blok="postContent"
      />
    </template>
    <div class="my-20">
      <h2 class="mx-5 mb-10 text-xl xl:text-2xl">
        {{ $t('outfits.more_outfits') }}
      </h2>
      <BasicSlider
        v-if="filteredOutfitData?.length"
        :items="filteredOutfitData"
        key-prop="_uid"
      >
        <template #item="{ item: outfitData }">
          <ImageText class="px-1" :blok="outfitData" />
        </template>
      </BasicSlider>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Product, ProductSibling } from '@scayle/storefront-nuxt'
import {
  getAppliedReductionsByCategory,
  getProductAndSiblingsColors,
} from '@scayle/storefront-nuxt'
import type {
  SbOutfit,
  SbContentPage,
  StoryblokStory,
} from '~/cms/storyblok/types/storyblok.d.ts'
import type { ListedProduct, ListItemType } from '~/composables/tracking/types'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'

const route = useRoute()
const slug = computed<string>(() => `/outfits/${route.params.slug}`)

useRouteCache((helper) => {
  helper.setMaxAge(3600).setCacheable()
})

const { setActiveProduct } = useUiState()
const {
  data: cmsData,
  fetchBySlug,
  metaLinks,
  excluded,
} = useCms<SbOutfit>(slug.value)
const {
  isActiveSalePeriod: _isActiveSalePeriod,
  fetch: fetchActiveSalePeriod,
} = useSmartShopConfiguration()
const {
  outfitsData,
  outfitsOverviewData,
  fetch: fetchOutfits,
  fetching,
} = useOutfitsFolder()

const { trackViewItemList } = useTrackingEvents()

const { name: translatedOverviewSlugName } = useTranslatedSlug(
  outfitsOverviewData as unknown as Ref<StoryblokStory<SbContentPage>>,
)
const { name: translatedSlugName } = useTranslatedSlug(
  cmsData as unknown as Ref<StoryblokStory<SbContentPage>>,
)

const filteredOutfitData = computed(() =>
  outfitsData.value.filter(
    (outfitData: { _uid: any }) =>
      outfitData._uid !== cmsData.value?.content?._uid,
  ),
)

const productIds = computed<number[]>(
  () =>
    cmsData.value?.content?.productIDs
      ?.split(',')
      .map((id: string) => parseInt(id)) || [],
)

const metaDataExtensions = computed(() => ({ link: metaLinks.value }))

useStoryMetaData({ data: cmsData, extensions: metaDataExtensions, fetching })

const {
  data: products,
  refresh: fetchProductsFromBapi,
  status: productsStatus,
} = useProductsByIds(
  {
    params: computed(() => ({
      ids: productIds.value,
      with: PRODUCT_DETAILS_WITH,
    })),
    options: { immediate: false },
  },
  `outfit-products-${route.params.slug}`,
)

const productsFetching = computed(() => productsStatus.value === 'pending')

const fetchProducts = async () => {
  if (productIds.value.length > 0) {
    await fetchProductsFromBapi()
  }
}

await fetchOnNavigation(async () => {
  await Promise.all([
    fetchBySlug(slug.value),
    fetchActiveSalePeriod(),
    fetchOutfits(),
  ])

  if (excluded.value || !cmsData.value) {
    throw createError({ statusCode: 404, statusMessage: 'Not found' })
  }

  await fetchProducts()
})

const getLowestPriceBetweenVariants = (product: Product) =>
  product.variants && getLowestPrice(product.variants)

const getSalesRelativeAmountByCategory = (
  product: Product,
  category: 'sale' | 'campaign',
) => {
  const variantsLowestPrice = getLowestPriceBetweenVariants(product)
  return variantsLowestPrice
    ? getAppliedReductionsByCategory(variantsLowestPrice, category)
    : []
}

const isSelectionModalOpen = ref(false)
const selectedProduct = ref<number | undefined>(undefined)
const trackingListType = computed<ListItemType>(() => 'outfit')
const trackingListName = computed(() => cmsData.value?.name ?? '')

const onProductClick = (product: Product) => {
  setActiveProduct(product)
  selectedProduct.value = product.id
  isSelectionModalOpen.value = true
}

const postListingContent = computed(
  () => cmsData.value?.content?.postContent || [],
)

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    de: '/outfits/[slug]',
    at: '/outfits/[slug]',
    ch: '/outfits/[slug]',
    'fr-ch': '/ensembles/[slug]',
    fr: '/ensembles/[slug]',
    'fr-be': '/ensembles/[slug]',
    be: '/outfits/[slug]',
    nl: '/outfits/[slug]',
    fi: '/asukokonaisuudet/[slug]',
  },
})

const { collectIntersection } = useProductIntersectionCollection({
  onIntersection: (items: ListedProduct[]) => {
    trackViewItemList({
      type: trackingListType.value,
      name: trackingListName.value,
      items,
      filters: '',
      filterOptions: '',
      sorting: '',
      totalItemQuantity: products.value?.length ?? 0,
      totalPages: 1,
      viewItemQuantity: products.value?.length ?? 0,
      currentPage: 1,
    })
  },
})

defineOptions({ name: 'OutfitsPage' })
definePageMeta({ pageType: 'outfit' })
</script>
